// An organisation's secrets (layout from design/isomorphic/src/pages/secrets.tsx): listed by
// anyone signed in, changed only by a linked device, which seals each value in the browser and
// signs each change.
import { Plus, Warning } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "rizzui";

import { orgsQuery } from "../api/queries";
import { useDevice } from "../device/context";
import { useT } from "../i18n";
import { useCurrentOrg } from "../layout/currentOrg";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { AddSecretDrawer } from "../secrets/AddSecretDrawer";
import { credentialsQuery, deleteSecret } from "../secrets/api";
import { DeleteSecretDialog } from "../secrets/DeleteSecretDialog";
import { EditSecretDrawer } from "../secrets/EditSecretDrawer";
import { ReplaceValueDrawer } from "../secrets/ReplaceValueForm";
import type { Signer } from "../secrets/requests";
import { ProtectionCard, RotationCard } from "../secrets/SecretsAside";
import { SecretsTable, type SignedActions } from "../secrets/SecretsTable";
import { type Credential, TEMPLATES, type TemplateId } from "../secrets/templates";
import { UsageLog } from "../secrets/UsageLog";
import { cardClass, IconTile } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";

const warn = (message: string) => toast(message, "warn");
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function SecretsPage() {
  const t = useT().secrets;
  const crumbs = sectionCrumbs("secrets");
  const { org } = useCurrentOrg();
  const orgs = useQuery(orgsQuery());
  const query = useQuery(credentialsQuery(org?.id));
  const queryClient = useQueryClient();
  const device = useDevice();
  // `?add=<template>` (e.g. from the VPN page) opens the drawer on that template.
  const [params] = useSearchParams();
  const asked = TEMPLATES.find((x) => x.id === params.get("add"))?.id as TemplateId | undefined;
  const [adding, setAdding] = useState(asked !== undefined);
  const [editing, setEditing] = useState<Credential>();
  const [replacing, setReplacing] = useState<Credential>();
  const [deleting, setDeleting] = useState<Credential>();
  const [usageId, setUsageId] = useState<string>();

  const signer: Signer | undefined =
    device.kind === "linked" ? { proto: device.proto, deviceId: device.device.deviceId, key: device.device.keys.auth.privateKey } : undefined;
  const signerName = device.kind === "linked" ? device.device.name : "";
  const credentials = query.data ?? [];
  const usage = credentials.find((c) => c.id === usageId) ?? credentials[0];
  // Every change is also a usage event, and the usage card stays on screen across changes.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["credentials"] }),
      queryClient.invalidateQueries({ queryKey: ["credential-usage"] }),
    ]);

  const actions: SignedActions | undefined = signer && { edit: setEditing, replace: setReplacing, remove: setDeleting };
  async function remove(credential: Credential) {
    if (!signer) return;
    try {
      await deleteSecret(signer, credential.id);
      await refresh();
      toast(t.page.deleted(credential.name));
    } catch (e) {
      warn(message(e));
    }
  }

  return (
    <>
      <PageHeader
        title={SECTIONS.secrets.title}
        breadcrumb={crumbs}
        description={t.page.description}
      >
        {signer && org ? (
          <Button className="gap-1.5" onClick={() => setAdding(true)}>
            <Plus weight="bold" className="h-4 w-4" /> {t.page.add}
          </Button>
        ) : null}
      </PageHeader>
      {device.kind === "loading" ? <Loading label={t.page.checkingKeys} /> : null}
      {device.kind === "unlinked" ? <UnlinkedNotice /> : null}
      {orgs.isError ? <ErrorState title={t.page.orgsError} message={orgs.error.message} /> : null}
      {orgs.data?.length === 0 ? (
        <EmptyState title={t.page.noOrgs}>{t.page.noOrgsBody}</EmptyState>
      ) : null}
      {org ? (
        <div className="grid gap-5 xl:grid-cols-12">
          <div className={cn(cardClass, "p-3 lg:p-4 xl:col-span-8")}>
            <SecretsTable query={query} onUsage={(c) => setUsageId(c.id)} actions={actions} />
          </div>
          <ProtectionCard className="xl:col-span-4" />
          {usage ? <UsageLog key={usage.id} credential={usage} className="xl:col-span-8" /> : null}
          {credentials.length > 0 ? <RotationCard credentials={credentials} className="xl:col-span-4" /> : null}
        </div>
      ) : null}
      {signer && org ? (
        <AddSecretDrawer
          open={adding}
          initial={asked}
          onClose={() => setAdding(false)}
          signer={signer}
          signerName={signerName}
          orgId={org.id}
          onError={warn}
          onSaved={(c) => {
            void refresh();
            toast(t.page.saved(c.name));
          }}
        />
      ) : null}
      {signer ? (
        <ReplaceValueDrawer
          signer={signer}
          signerName={signerName}
          credential={replacing}
          onError={warn}
          onCancel={() => setReplacing(undefined)}
          onSaved={(c) => {
            setReplacing(undefined);
            void refresh();
            toast(t.page.replaced(c.name));
          }}
        />
      ) : null}
      {signer ? (
        <EditSecretDrawer
          signer={signer}
          signerName={signerName}
          credential={editing}
          onClose={() => setEditing(undefined)}
          onUpdated={(c) => {
            setEditing(undefined);
            void refresh();
            toast(t.page.updated(c.name));
          }}
          onReplaced={(c) => {
            setEditing(undefined);
            void refresh();
            toast(t.page.replaced(c.name));
          }}
        />
      ) : null}
      <DeleteSecretDialog credential={deleting} onClose={() => setDeleting(undefined)} onConfirm={remove} />
    </>
  );
}

function UnlinkedNotice() {
  const t = useT().secrets.page;
  return (
    <section className={cn(cardClass, "mb-5 flex items-start gap-3 p-5")} aria-label={t.unlinkedLabel}>
      <IconTile className="bg-orange-lighter text-orange-dark">
        <Warning weight="duotone" />
      </IconTile>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-900">{t.unlinkedTitle}</h3>
        <p className="mb-0 mt-1 text-[13px] text-gray-500">
          {t.unlinkedBody(
            <Link to="/devices" className="font-medium text-primary">
              {t.unlinkedLink}
            </Link>,
          )}
        </p>
      </div>
    </section>
  );
}
